import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import Pagination from "@/global/tables/Pagination";
import BlobLoader from "@/components/common/Loader/BlobLoader";
import PrescriptionAuditTrailCard from "@/components/common/ViewPAF/components/PrescriptionAuditTrailCard";
import { handleCatch } from "@/shared/helpers";
import { fetchAuditTrail } from "@/services/auditTrail";

const DEFAULT_PAGE_SIZE = 20;

/** Fields scanned by the on-page search box. */
const SEARCHABLE_KEYS = [
  "prescription_id",
  "patient_id",
  "submitted_by",
  "received_by",
  "approved_by",
  "rejected_by",
  "reverted_by",
  "dispensed_by",
  "molecule",
  "indication",
  "status",
];

function entryMatchesQuery(entry, query) {
  if (!query) return true;
  const needle = query.toLowerCase();
  return SEARCHABLE_KEYS.some((key) => {
    const value = entry?.[key];
    return value != null && String(value).toLowerCase().includes(needle);
  });
}

const AuditTrail = () => {
  const [entries, setEntries] = useState([]);
  const [count, setCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetchAuditTrail({
      page: pageIndex + 1,
      pageSize,
      signal: controller.signal,
    })
      .then((data) => {
        setEntries(data.results);
        setCount(data.count);
      })
      .catch((error) => {
        if (error?.code === "ERR_CANCELED") return;
        handleCatch(error);
        setEntries([]);
        setCount(0);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [pageIndex, pageSize]);

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setPageIndex(0);
  }, []);

  const visibleEntries = useMemo(
    () => entries.filter((entry) => entryMatchesQuery(entry, search.trim())),
    [entries, search]
  );

  const startRow = count === 0 ? 0 : pageIndex * pageSize + 1;
  const endRow = Math.min((pageIndex + 1) * pageSize, count);

  return (
    <div className="p-8" data-testid="audit-trail-page">
      <div className="mx-auto max-w-[1200px]">
        <div
          className="mb-4 flex items-center gap-2 rounded-full border border-[#cfe3ef] bg-white px-4 py-2 shadow-sm"
          data-testid="audit-trail-search"
        >
          <Search className="h-4 w-4 shrink-0 text-[#8c8c8c]" aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search prescription, patient, user, action"
            className="w-full bg-transparent text-sm text-[#3c3c3c] outline-none placeholder:text-[#8c8c8c]"
            aria-label="Search audit trail"
            data-testid="audit-trail-search-input"
          />
        </div>

        <div
          className="rounded-2xl bg-white p-5 shadow-sm"
          data-testid="audit-trail-list"
        >
          <h2 className="mb-4 text-lg font-bold text-[#00567a]">Audit Trail</h2>

          {loading ? (
            <div
              className="flex justify-center py-16"
              data-testid="audit-trail-loading"
            >
              <BlobLoader size={96} showLoadingText={false} />
            </div>
          ) : visibleEntries.length ? (
            <div className="space-y-4" data-testid="audit-trail-entries">
              {visibleEntries.map((entry, index) => (
                <PrescriptionAuditTrailCard
                  key={`${entry.prescription_id ?? "audit"}-${index}`}
                  entry={entry}
                />
              ))}
            </div>
          ) : (
            <div
              className="py-16 text-center text-sm text-[#8c8c8c]"
              data-testid="audit-trail-empty"
            >
              {search.trim()
                ? "No audit trail entries match your search on this page."
                : "No audit trail entries available."}
            </div>
          )}

          {!loading && count > 0 && (
            <div data-testid="audit-trail-pagination">
              <Pagination
                pageIndex={pageIndex}
                pageSize={pageSize}
                totalRows={count}
                startRow={startRow}
                endRow={endRow}
                pageSizes={["10", "20", "30", "50", "100"]}
                canPreviousPage={pageIndex > 0}
                canNextPage={(pageIndex + 1) * pageSize < count}
                onPreviousPage={() => setPageIndex((prev) => Math.max(0, prev - 1))}
                onNextPage={() => setPageIndex((prev) => prev + 1)}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditTrail;
